import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { RefObject, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { buildSidebarExploreInTableSearch } from "../buildSidebarExploreInTableSearch";
import { SidebarStructuredSearchControls } from "../SidebarStructuredSearchControls";
import { appendNestedKeyEqualsStructuredFilter } from "../appendNestedKeyEqualsStructuredFilter";
import { useSidebarStructuredSearchRefresh } from "../hooks/useSidebarStructuredSearchRefresh";

import { CLOSE_REVIEW_LOGS_DRAWER_EVENT } from "./constants";

import { isPostgresOnlySearch } from "@/routes/request-logs/search/older-data";
import { buildStructuredSearchPayload } from "@/routes/request-logs/search/structured-search-payload";
import { useStructuredRequestLogsRefresh } from "@/routes/request-logs/search/hooks/useStructuredRequestLogsRefresh";
import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import {
  ADD_REQUESTS_STRUCTURED_FILTER_EVENT,
  type AddRequestsStructuredFilterEventDetail,
} from "@/components/SidebarSearchLayout/sidebar-tab-context";
import {
  createEmptySidebarStructuredInfiniteData,
  getSidebarStructuredRequestLogsQueryKey,
} from "@/components/SidebarSearchLayout/Sidebar/hooks/useStructuredSidebarRequestLogs";
import { SESSION_STORAGE } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import type { UseStructuredRequestsPaginatedOptions } from "@/queries/requests/useStructuredRequestsPaginated";
import { preloadAnalyticsPage } from "@/routes/analytics/loadAnalyticsPage";
import { REQUEST_LOGS_SIDEBAR_PAGE_SIZE } from "@/utils/requestLogUrlParams";

export interface ReviewLogsDrawerControlsProps {
  inputRef: RefObject<HTMLInputElement | null>;
  onRefresh?: () => void;
}

export const ReviewLogsDrawerControls = observer(
  ({ inputRef, onRefresh }: ReviewLogsDrawerControlsProps) => {
    const navigate = useNavigate();
    const { workspaceId } = useParams();
    const location = useLocation();
    const structuredSearchStore = useStructuredSearchStore();
    const queryClient = useQueryClient();
    const { clearNewRequestsBadge } = useStructuredRequestLogsRefresh();
    const auth = useAuth();
    const userToken = auth?.userToken ?? "";
    const user = useUser();
    const activeWorkspaceId = user?.activeWorkspaceId;
    const activeWorkspace = user?.activeWorkspace;

    useEffect(() => {
      if (!workspaceId) {
        return;
      }
      preloadAnalyticsPage();
    }, [workspaceId]);

    useEffect(() => {
      const handleAddStructuredFilter = (event: Event) => {
        const { detail } = event as CustomEvent<AddRequestsStructuredFilterEventDetail>;
        if (!detail) {
          return;
        }

        appendNestedKeyEqualsStructuredFilter(structuredSearchStore, detail);
      };

      window.addEventListener(ADD_REQUESTS_STRUCTURED_FILTER_EVENT, handleAddStructuredFilter);
      return () =>
        window.removeEventListener(ADD_REQUESTS_STRUCTURED_FILTER_EVENT, handleAddStructuredFilter);
    }, [structuredSearchStore]);

    const handleExploreInTablePreload = useCallback(() => {
      preloadAnalyticsPage();
    }, []);

    const handleExploreInTable = () => {
      if (!workspaceId) {
        return;
      }

      preloadAnalyticsPage();
      window.dispatchEvent(new Event(CLOSE_REVIEW_LOGS_DRAWER_EVENT));
      if (structuredSearchStore.editingFilterId) {
        structuredSearchStore.cancelEdit();
      } else {
        structuredSearchStore.cancelBuilder();
      }
      inputRef.current?.blur();

      const search = buildSidebarExploreInTableSearch({
        sidebarStore: structuredSearchStore,
        returnPathname: location.pathname,
        returnSearch: location.search,
      });
      sessionStorage.setItem(
        SESSION_STORAGE.REQUESTS_ANALYTICS_RETURN_URL,
        `${location.pathname}${location.search}${location.hash}`,
      );
      navigate(`/workspace/${workspaceId}/request-logs${search}`);
    };

    const resetSidebarQuery = useCallback(() => {
      if (!userToken || typeof activeWorkspaceId !== "number") {
        return;
      }

      const initialPageNumber = structuredSearchStore.pageIndex;
      const sidebarPageSize =
        structuredSearchStore.pageSize > 0
          ? structuredSearchStore.pageSize
          : REQUEST_LOGS_SIDEBAR_PAGE_SIZE;

      const structuredSearchPayload = buildStructuredSearchPayload(
        structuredSearchStore.filters,
        structuredSearchStore.dateRange,
      );
      const requestSearchParams: UseStructuredRequestsPaginatedOptions = {
        ...(structuredSearchStore.q ? { q: structuredSearchStore.q } : {}),
        filterGroup: structuredSearchPayload.filterGroup,
        includeCount: !isPostgresOnlySearch({
          dateRange: structuredSearchStore.dateRange,
          cutoverDate: activeWorkspace?.search_cutover_date,
        }),
      };

      const queryKey = getSidebarStructuredRequestLogsQueryKey({
        userToken,
        requestSearchParams,
        activeWorkspaceId,
        initialPageNumber,
        sidebarPageSize,
      });

      queryClient.setQueryData(
        queryKey,
        createEmptySidebarStructuredInfiniteData(initialPageNumber, sidebarPageSize),
      );

      void queryClient.refetchQueries({ queryKey, exact: true });
    }, [
      activeWorkspace?.search_cutover_date,
      activeWorkspaceId,
      queryClient,
      structuredSearchStore,
      userToken,
    ]);

    const handleRefresh = useSidebarStructuredSearchRefresh({
      onRefresh,
      clearNewDataBadge: clearNewRequestsBadge,
      resetSidebarQuery,
    });

    return (
      <SidebarStructuredSearchControls
        inputRef={inputRef}
        onExploreInTable={handleExploreInTable}
        onExploreInTablePreload={handleExploreInTablePreload}
        onRefresh={handleRefresh}
      />
    );
  },
);
